import { useEffect, useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { InView } from 'react-intersection-observer'
import { useMobile } from 'src/hooks/useMobile'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import ProductCardV3 from 'src/components/restructure/product/ProductCardV3/ProductCardV3'
import type { Product } from 'src/components/restructure/product/types/product'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'

import { BannerCard } from './BannerCard'
import { fetchTabProducts, getValidTabs, getVisibleProducts } from './helpers'
import type { BannerProductTabsProps } from './types'

export default function BannerProductTabs({
  section,
  enableSection,
  showOnDesktop = true,
  showOnMobile = true,
  backgroundColor,
  header,
  tabs,
}: Readonly<BannerProductTabsProps>) {
  const { isMobile } = useMobile()
  const { pageData } = useGlobalContext()

  const validTabs = useMemo(() => getValidTabs(tabs), [tabs])

  const [activeTab, setActiveTab] = useState(0)
  const [loadingTab, setLoadingTab] = useState(false)

  const [productsByTab, setProductsByTab] = useState<Record<number, Product[]>>(
    {}
  )

  const dataSection = section ?? 'Banner Product Tabs'

  const shouldRender =
    enableSection &&
    validTabs.length >= 2 &&
    (isMobile ? showOnMobile : showOnDesktop)

  useEffect(() => {
    const currentTab = validTabs[activeTab]

    if (!shouldRender || !currentTab || productsByTab[activeTab]) {
      return
    }

    let isActive = true

    setLoadingTab(true)
    fetchTabProducts(currentTab.products).then((products) => {
      if (!isActive) {
        return
      }

      setProductsByTab((prev) => ({ ...prev, [activeTab]: products }))
      setLoadingTab(false)
    })

    return () => {
      isActive = false
    }
  }, [activeTab, validTabs, productsByTab, shouldRender])

  const sendPromoFeatureViewedEvent = () => {
    if (!section) {
      return
    }

    const eventData = {
      ...getStandardObject(pageData),
      ...getFeatureObject({
        section,
        name: header?.title ?? 'N/A',
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  const sendPromotionalElementSelected = (name: string, index: number) => {
    if (!section) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...getStandardObject(pageData),
        ...getFeatureObject({
          section,
          name,
          index,
        }),
      },
    })
  }

  if (!shouldRender) {
    return null
  }

  const currentBanner = validTabs[activeTab]?.banner

  const isBannerEnabled = Boolean(
    currentBanner?.enableBanner &&
      (currentBanner?.backgroundImageDesktop ||
        currentBanner?.backgroundImageMobile)
  )

  const currentProducts = productsByTab[activeTab] ?? []
  const visibleProducts = getVisibleProducts(currentProducts, isBannerEnabled)

  return (
    <InView
      as="section"
      triggerOnce
      style={{ backgroundColor }}
      className="mx-auto w-full"
      data-section={dataSection}
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
    >
      <div className="mx-auto w-full max-w-[1680px] px-4 py-8 md:px-8 md:py-14 xl:px-12 2xl:px-0">
        <header className="mb-6 flex flex-col gap-[1.125rem] md:mb-8 md:flex-row md:items-center md:justify-start">
          {(header?.title || header?.subtitle) && (
            <div className="flex items-center gap-2 md:gap-5   font-inter text-[#222222]">
              {header?.title && (
                <h2 className="text-sm font-semibold leading-6 md:text-[1.625rem] md:leading-9">
                  {header.title}
                </h2>
              )}
              {header?.title && header?.subtitle && (
                <span
                  aria-hidden="true"
                  className="h-6 w-px bg-current opacity-30 md:h-8"
                />
              )}
              {header?.subtitle && (
                <p className="text-sm font-semibold leading-7 md:text-[1.625rem] md:leading-9">
                  {header.subtitle}
                </p>
              )}
            </div>
          )}

          <div
            role="group"
            aria-label={header?.title ?? 'Categorias da vitrine'}
            className="flex gap-2 overflow-x-auto no-scrollbar"
          >
            {validTabs.map((tab, index) => {
              const isSelected = index === activeTab

              return (
                <button
                  key={`tab-${tab.label}-${index}`}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => {
                    setActiveTab(index)
                    sendPromotionalElementSelected(tab.label ?? 'N/A', index)
                  }}
                  className={`whitespace-nowrap rounded-full px-5 py-2 font-inter text-[14px] font-semibold text-[#101010] transition-colors ${
                    isSelected
                      ? 'border-2 border-[#101010]'
                      : 'border border-restructure-border-secondary hover:border-restructure-border-primary'
                  }`}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        </header>

        {loadingTab && !productsByTab[activeTab] ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={`skeleton-${index}`}
                height={isMobile ? 280 : 500}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {visibleProducts.map((product, index) => (
              <ProductCardV3
                key={product.productId}
                product={product}
                index={index}
                title={header?.title}
              />
            ))}

            {isBannerEnabled && currentBanner && (
              <BannerCard
                banner={currentBanner}
                onSelect={() =>
                  sendPromotionalElementSelected(
                    header?.title ?? 'Banner',
                    visibleProducts.length
                  )
                }
              />
            )}
          </div>
        )}
      </div>
    </InView>
  )
}
